import { graphql, useStaticQuery } from "gatsby"
import React, { useEffect, useState } from "react"
import chevrondown from "../../assets/svg/chevron-down.svg"
import chevronup from "../../assets/svg/chevron-up.svg"
import {
  useI18next,
  useTranslation,
} from "../../plugins/i18next/ea-react-i18next"
import { AllFileData } from "../../utils/core"
import "./FeaturesSection.module.css"
import {
  getDetailsByMenuItem,
  getMenuByType,
  getProductClass,
} from "./KeyFeaturesList"
import { getImageForFeature, ProductType } from "./ProductList"
import Button from "../Start/Button"

const ProductFeatureList: React.FC<{ product: ProductType }> = ({
  product,
}) => {
  const [features, setFeatures] = useState<string[]>([])
  const [imagePath, setimagePath] = useState<string>("")
  const [activeTab, setActiveTab] = useState<string>("")
  const [selectedFeature, setSelectedFeature] = useState<string>("")
  const [activeMenu, setActiveMenu] = useState("")
  const [activeMenus, setActiveMenus] = useState<string[]>([])

  const { i18n } = useI18next()
  const currentLanguage = i18n.language

  const toggleFeatures = (menuitem: string) => {
    setActiveMenus(prevActiveMenus => {
      const isCurrentlyOpen = prevActiveMenus.includes(menuitem)
      const newActiveMenus = isCurrentlyOpen
        ? prevActiveMenus.filter(item => item !== menuitem) // Close if already open
        : [...prevActiveMenus, menuitem] // Open if closed

      // Update class manually after state update

      const contentItem = document.getElementById(`feature-${menuitem}`)
      if (contentItem) {
        contentItem.classList.remove("open", "close")
        contentItem.classList.add(isCurrentlyOpen ? "close" : "open")
      }
      return newActiveMenus
    })

    // Smooth scrolling for better mobile UX
    setTimeout(() => {
      const selectedItem = document.getElementById(`menuitem-${menuitem}`)
      if (selectedItem) {
        selectedItem.scrollIntoView({ behavior: "smooth", block: "nearest" })
      }
    }, 100)
  }

  const getFeaturesForMenu = (menuitem: string): string[] => {
    return activeMenus.includes(menuitem)
      ? getDetailsByMenuItem(product, menuitem) || []
      : []
  }

  const handleFeatureClick = (featureKey: string) => {
    const key = featureKey.split(".").pop() || ""
    const imgPath = getImageForFeature(key)
    setimagePath(imgPath)
    setSelectedFeature(featureKey)
  }

  const handleFeatureHover = (featureKey: string) => {
    const key = featureKey.split(".").pop() || ""
    const imgPath = getImageForFeature(key)
    setimagePath(imgPath)
  }

  const { t } = useTranslation("components", {
    keyPrefix: "products.productFeatures",
  })
  const menulist = getMenuByType(product)
  const productClass = getProductClass(product)

  const getFeatures = (menuitem: string) => {
    const featureList = getDetailsByMenuItem(product, menuitem)
    setActiveTab(menuitem)
    setFeatures(featureList)
    handleFeatureClick(featureList[0])
  }

  const data: AllFileData = useStaticQuery(graphql`
    {
      allFile(
        filter: {
          extension: { eq: "pdf" }
          sourceInstanceName: { eq: "featurelistdownloads" }
        }
      ) {
        edges {
          node {
            name
            publicURL
          }
        }
      }
    }
  `)
  const downloadFile = data.allFile.edges.filter(
    p =>
      `${p.node.name}.pdf` ===
      (t(`${productClass}.downloadfeatureList`) as string)
  )[0]
  const productFeatureListDownload = downloadFile?.node?.publicURL

  useEffect(() => {
    setActiveTab(menulist[0])
    getFeatures(menulist[0])
    setActiveMenu(menulist[0])
  }, [])

  return (
    <section className="about-area res-pt-0 pb-100">
      <div className="container-fluid hero-container">
        <div className="section-header">
          <h1>{t("mainText")}</h1>
          <h4>{t(`${productClass}.solutions`)}</h4>
          <p className="landing-header-subtext">
            {t(`${productClass}.mainSubText`)}
          </p>
        </div>
        <div className="desktop">
          <div className="product-feature-display">
            <div className="menu">
              {menulist.map(menuitem => (
                <a
                  onClick={e => {
                    e.preventDefault()
                    getFeatures(menuitem)
                  }}
                  className={`menu-item ${activeTab == menuitem ? "active" : ""
                    } ${productClass}`}
                >
                  <h4>{t(`${product}.${menuitem}.tabName`)}</h4>
                </a>
              ))}
            </div>
            <div className="feature-list-row">
              <div className="feature-list">
                {features.map(feature => (
                  <div
                    className={`feature-list-item ${productClass}`}
                    onMouseEnter={() => handleFeatureHover(feature)}
                  >
                    <h4>{t(`${feature}.title`)}</h4>
                    <p>{t(`${feature}.details`)}</p>
                  </div>
                ))}
              </div>
              <div className="image-box">
                <img src={imagePath} alt="Feature" />
              </div>
            </div>
            {downloadFile && (
              <div className="footer">
                <div className="subscribe-content">
                  <Button text={t("downloadList")} icon="flaticon-right" external={true} href={productFeatureListDownload} />
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="mobile">
          <div className="product-feature-display">
            <div className="accordion-menu">
              {menulist.map(menuitem => (
                <div
                  key={menuitem}
                  className="accordion-item"
                  id={`menuitem-${menuitem}`}
                >
                  <a
                    href="#"
                    className={`accordion-header ${productClass}`}
                    onClick={e => {
                      e.preventDefault()
                      toggleFeatures(menuitem)
                    }}
                  >
                    <h4>{t(`${product}.${menuitem}.tabName`)}</h4>
                    {activeMenus.includes(menuitem) ? (
                      <img
                        src={chevronup}
                        alt="collapse"
                        className={`accordion-header-icon ${productClass}`}
                      />
                    ) : (
                      <img
                        src={chevrondown}
                        alt="expand"
                        className={`accordion-header-icon ${productClass}`}
                      />
                    )}
                  </a>
                  <div
                    id={`feature-${menuitem}`}
                    className={`accordion-content`}
                  >
                    {activeMenus.includes(menuitem) && (
                      <div className="accordion-feature-list">
                        {getFeaturesForMenu(menuitem).map(feature => (
                          <div
                            key={feature}
                            className={`accordion-feature-item ${productClass}`}
                          >
                            <p
                              className={`accordion-content-title ${productClass}`}
                            >
                              {t(`${feature}.title`)}
                            </p>
                            <p className="accordion-content-details">
                              {t(`${feature}.details`)}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="footer">
              <div className="subscribe-content">
                <Button text={t("downloadList")} icon="flaticon-right" external={true} href={productFeatureListDownload} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default ProductFeatureList
